import {
  SearchFilterCondition,
  SearchFilterConditionCustomAttributeSelect,
  SearchFilterValue,
} from 'lib-shared-types-api';
import { useMemo } from 'react';

import { isCustomAttributeCondition } from 'app/views/layout/views/search/utils/filters/filterTypeGuards';
import { convertTreeToFlatItems } from 'utils/tree';

import { useFilterConditionCustomAttribute } from '../../../../filters/components/condition/utils/useFilterConditionCustomAttribute';
import {
  filterFieldAssets,
  filterOperatorLabels,
} from '../../../../filters/utils/definitions';
import {
  SavedSearchFiltersItem,
  SavedSearchFiltersItemProps,
} from '../item/savedSearchFiltersItem';

type SavedSearchFiltersConditionProps = {
  condition: SearchFilterCondition;
} & Pick<SavedSearchFiltersItemProps, 'variant' | 'onDelete'>;

export const SavedSearchFiltersCondition = ({
  condition,
  ...itemProps
}: SavedSearchFiltersConditionProps): JSX.Element => {
  const customAttribute = useFilterConditionCustomAttribute(condition);

  const stringifyValues = (values: SearchFilterValue[]): string => {
    return values.map((item) => item.label || item.value).join(', ');
  };

  const label = useMemo((): string => {
    if (isCustomAttributeCondition(condition)) {
      return customAttribute?.label ?? `Custom attribute`;
    }
    return filterFieldAssets[condition.field].label;
  }, [condition, customAttribute]);

  const value = useMemo((): string => {
    if (!isCustomAttributeCondition(condition)) {
      return stringifyValues(condition.values);
    }

    if (customAttribute?.type === 'select') {
      const flatOptions = convertTreeToFlatItems(customAttribute.options, {
        childrenKey: 'options',
      });

      const values = (
        condition as SearchFilterConditionCustomAttributeSelect
      ).values.map(({ value }) => ({
        label: value
          .map(
            (value) =>
              flatOptions.find((option) => option.value === value)?.label ?? '',
          )
          .join(' / '),
        value,
      }));

      return stringifyValues(values);
    }

    return stringifyValues(condition.values);
  }, [condition, customAttribute]);

  return (
    <SavedSearchFiltersItem
      {...itemProps}
      label={label}
      value={value}
      operator={filterOperatorLabels[condition.operator]}
    />
  );
};
